import { useEffect, useState, useImperativeHandle, forwardRef } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Send, MessageCircle, Calendar, CheckCircle2, Euro, TrendingUp } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

export interface VcKpi {
  sent: number;
  replied: number;
  call: number;
  signed: number;
  rejected: number;
  total_signed_eur: number;
  total_potential_eur: number;
}

export interface VcPipelineKpiHandle {
  refresh: () => Promise<void>;
}

const fmtEur = (n: number) => new Intl.NumberFormat("fr-FR", { notation: "compact", maximumFractionDigits: 1 }).format(n) + "€";

const VcPipelineKpi = forwardRef<VcPipelineKpiHandle, { onLoaded?: (byFund: Record<string, any>) => void }>(
  ({ onLoaded }, ref) => {
    const [kpi, setKpi] = useState<VcKpi | null>(null);

    const load = async () => {
      try {
        const r = await fetch(`${BACKEND_URL}/api/preseed/pipeline`, { credentials: "include" });
        if (!r.ok) return;
        const d = await r.json();
        setKpi(d.kpi);
        onLoaded?.(d.by_fund || {});
      } catch { /* tolerate */ }
    };

    useImperativeHandle(ref, () => ({ refresh: load }), []);
    useEffect(() => { load(); }, []);

    const replyRate = kpi && kpi.sent > 0 ? Math.round((kpi.replied / kpi.sent) * 100) : 0;

    const items = [
      { key: "sent", label: "Envoyés", value: kpi?.sent ?? 0, icon: Send, color: "text-sky-400" },
      { key: "replied", label: "Répondus", value: kpi?.replied ?? 0, icon: MessageCircle, color: "text-amber-400" },
      { key: "call", label: "Calls bookés", value: kpi?.call ?? 0, icon: Calendar, color: "text-violet-400" },
      { key: "signed", label: "Signés", value: kpi?.signed ?? 0, icon: CheckCircle2, color: "text-emerald-400" },
    ];

    return (
      <Card className="bg-card border-border" data-testid="vc-pipeline-kpi">
        <CardContent className="py-3 px-4">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-primary" />
              <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Pipeline VC — en temps réel
              </span>
            </div>
            <div className="flex items-center gap-4 text-[11px] text-muted-foreground flex-wrap">
              <span>Taux de réponse : <b className="text-foreground">{replyRate}%</b></span>
              <span className="flex items-center gap-1">
                <Euro className="h-3 w-3" /> Signé : <b className="text-emerald-400">{fmtEur(kpi?.total_signed_eur || 0)}</b>
              </span>
              <span className="flex items-center gap-1">
                Potentiel : <b className="text-amber-400">{fmtEur(kpi?.total_potential_eur || 0)}</b>
              </span>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-3">
            {items.map(it => (
              <div
                key={it.key}
                className="border border-border rounded-lg p-3 flex items-center gap-3 bg-background/30"
                data-testid={`kpi-${it.key}`}
              >
                <div className={`w-8 h-8 rounded-md bg-primary/10 flex items-center justify-center ${it.color}`}>
                  <it.icon className="h-4 w-4" />
                </div>
                <div>
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wide">{it.label}</p>
                  <p className="text-xl font-bold leading-none mt-0.5">{it.value}</p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }
);
VcPipelineKpi.displayName = "VcPipelineKpi";

export default VcPipelineKpi;
